





























































































import { Component, Prop, Vue } from "vue-property-decorator";
import {
  COLOR_MIRA_BAR,
  COLOR_MIRA_BAR_DARK,
  COLOR_MIRA_BAR_TRACK,
  COLOR_MIRA_BAR_TRACK_DARK,
  COLOR_MIRA_COMPARISON_MARKER,
  COLOR_MIRA_COMPARISON_MARKER_DARK,
  COLOR_MIRA_TREND_PERIOD,
  COLOR_MIRA_TREND_PERIOD_DARK,
} from "@/utils/colors";
import { MIRA_COMPARISON_FOLD, MiraComparisonMarkers, MiraComparisonRow, MiraComparisonView } from "@/utils/MiraComparisonView";
import { miraDeltaColor } from "@/utils/MiraDataQualityView";
import { themed } from "@/utils/Theme";

/**
 * The comparison card: one Mira figure set side by side across the member clients of the group, a bar
 * per member against the group total. It replaces the Verlauf card when a comparison is chosen.
 *
 * The group total is the reference row (bold, with the header-grey track; a total without a bar); the
 * members follow as bars scaled to the largest of them, best first (#2610112), a total's rows each with
 * their share, the first ten shown with an "Alle anzeigen" fold.
 * Members with no value in the period are named on one "Ohne Wert" line, never as zero bars. All of the
 * logic is in the pure {@link MiraComparisonView}; this component only draws it, so changes are neutral
 * grey (a member higher or lower than another is neither good nor bad).
 */
@Component
export default class MiraComparisonCardComponent extends Vue {
  @Prop({ required: true })
  title!: string;

  @Prop({ default: null })
  subtitle!: string | null;

  @Prop({ default: false })
  loading!: boolean;

  @Prop({ default: null })
  view!: MiraComparisonView | null;

  /** The empty-state wording, so the branch comparison can say "Standort" where the member view says "Mandant". */
  @Prop({ default: null })
  emptyText!: string | null;

  /**
   * The stable colour per entity id (the same map the grouped bars use), for the small colour dot on each
   * row, so a row's colour matches its bar. Empty where the screen does not supply it (then no dots show).
   */
  @Prop({ default: () => ({}) })
  entityColors!: Record<string, string>;

  expanded = false;
  fold = MIRA_COMPARISON_FOLD;

  get reference(): MiraComparisonRow | null {
    return this.view?.reference ?? null;
  }

  /** A total's reference row is a figure without a bar (#2610112 X1). */
  get referenceWithoutBar(): boolean {
    return this.view?.referenceWithoutBar ?? false;
  }

  /** Branch comparison only: the figure has no branch comparison, so the card shows a reason line. */
  get noComparisonForMetric(): boolean {
    return this.view?.noComparisonForMetric ?? false;
  }

  /** The empty-state text: the caller's wording where given (branches), else the member default. */
  get emptyLabel(): string {
    return this.emptyText ?? this.$t("mira.comparison.empty").toString();
  }

  get rows(): MiraComparisonRow[] {
    return this.view?.rows ?? [];
  }

  get withoutValue(): string[] {
    return this.view?.withoutValue ?? [];
  }

  /** The member rows up to the fold, unless the user has expanded them. */
  get visibleRows(): MiraComparisonRow[] {
    return this.expanded ? this.rows : this.rows.slice(0, this.fold);
  }

  /** The whole-group change column shows only when the period has a comparison, uniform across rows. */
  get showDelta(): boolean {
    return this.rows.some((row) => row.showDelta) || (this.reference?.showDelta ?? false);
  }

  get barColor(): string {
    return themed(COLOR_MIRA_BAR, COLOR_MIRA_BAR_DARK);
  }

  /** The reference's spread, or `null` where the figure has none or percentiles are not allowed. */
  /** The company's own row after the others (the Markt, a member's group; #2610137). */
  get referenceLast(): boolean {
    return this.view?.referenceLast ?? false;
  }

  get markers(): MiraComparisonMarkers | null {
    return this.view?.markers ?? null;
  }

  /**
   * The three marker lines on a track, positioned on the rows' bar scale: the row's own spread where it
   * carries one (the Markt, #2610072), else the reference's, drawn on every track (Hans, R3).
   */
  markerLines(row: MiraComparisonRow): Array<{ key: string; left: string }> {
    const markers = row.markers ?? this.markers;
    if (!markers) return [];
    return (["p15", "median", "p85"] as const).map((key) => ({ key, left: this.fillWidth(markers[key]) }));
  }

  get markerColor(): string {
    return themed(COLOR_MIRA_COMPARISON_MARKER, COLOR_MIRA_COMPARISON_MARKER_DARK);
  }

  get memberTrackColor(): string {
    return themed(COLOR_MIRA_BAR_TRACK, COLOR_MIRA_BAR_TRACK_DARK);
  }

  get referenceTrackColor(): string {
    return themed(COLOR_MIRA_TREND_PERIOD, COLOR_MIRA_TREND_PERIOD_DARK);
  }

  /** Neutral grey, the same colour a Mira neutral change uses everywhere. */
  get deltaColor(): string {
    return miraDeltaColor("flat", this.$vuetify.theme.dark, "neutral");
  }

  deltaIcon(direction: "up" | "down" | "flat"): string {
    return direction === "up" ? "mdi-arrow-up" : "mdi-arrow-down";
  }

  fillWidth(fraction: number): string {
    return `${Math.max(0, Math.min(1, fraction)) * 100}%`;
  }

  /** The row's stable colour from the shared map, or `null` where none is supplied (then no dot shows). */
  dotColor(id: string): string | null {
    return this.entityColors[id] ?? null;
  }

  toggle() {
    this.expanded = !this.expanded;
  }
}
